import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { PermissionGuard } from '@/features/auth';
import { Alert, Button, Tooltip } from '@/shared/components';
import { readApiError, DEFAULT_ERROR_MESSAGE } from '@/services/api';
import { handleUserMutationError } from '@/features/users/services/userErrorHandler';
import { useDeactivateDealerMutation } from '../hooks/useDeactivateDealerMutation';
import { useReactivateDealerMutation } from '../hooks/useReactivateDealerMutation';
import { useDeleteDealerMutation } from '../hooks/useDeleteDealerMutation';

interface Props {
  dealerId: string;
  status: 'active' | 'inactive' | 'pending' | string | undefined;
  /** Ownership children count. Deactivate/Delete are blocked while > 0,
   * mirroring the backend delete/type-change guard (DealerParentageGuard). */
  childDealersCount?: number;
}

const CHILDREN_TOOLTIP = 'Detach owned dealers before deactivating or deleting this dealer.';

/**
 * Extract a human-readable guard reason for the panel: the field-map guard
 * (errors.dealer) first, then readApiError; null when nothing is readable.
 */
function extractInlineError(error: unknown): string | null {
  const result = handleUserMutationError(error);
  const fieldMessage = result.fieldErrors?.dealer?.[0];
  if (fieldMessage) return fieldMessage;

  const { message } = readApiError((error as AxiosError).response?.data);
  return message === DEFAULT_ERROR_MESSAGE ? null : message;
}

export function DealerStatusPanel({ dealerId, status, childDealersCount = 0 }: Props) {
  const navigate = useNavigate();
  const deactivateMutation = useDeactivateDealerMutation();
  const reactivateMutation = useReactivateDealerMutation();
  const deleteMutation = useDeleteDealerMutation();
  const [actionError, setActionError] = useState<string | null>(null);

  // A dealer is "Retired" only when its status is Inactive. Every other status —
  // Active, Active for Sales, Active for Invoicing, Pending — is a live state that
  // shows Deactivate (and no Delete). Reactivate + Delete are for retired dealers.
  // Status catalogue: backend dealers-service DealerStatusesSeeder.
  const isLive = status !== 'inactive';
  const hasChildDealers = childDealersCount > 0;

  const handleStatusChange = () => {
    setActionError(null);
    if (isLive) {
      if (!window.confirm('Are you sure you want to deactivate this dealer? They will no longer appear in active listings until reactivated.')) {
        return;
      }
      deactivateMutation.mutate(dealerId, {
        onError: (error: unknown) => {
          const message = extractInlineError(error);
          if (message) setActionError(message);
        },
      });
    } else {
      if (!window.confirm('Are you sure you want to reactivate this dealer?')) {
        return;
      }
      reactivateMutation.mutate(dealerId);
    }
  };

  const handleDelete = () => {
    setActionError(null);
    if (!window.confirm('Are you sure you want to delete this dealer? The record will be soft-deleted and hidden from listings.')) {
      return;
    }
    deleteMutation.mutate(dealerId, {
      onSuccess: () => navigate('/admin/dealers'),
      onError: (error: unknown) => {
        const message = extractInlineError(error);
        if (message) setActionError(message);
      },
    });
  };

  const deactivateBlocked = isLive && hasChildDealers;

  const statusButton = (
    <Button
      size="sm"
      variant={isLive ? 'danger' : 'primary'}
      onClick={handleStatusChange}
      disabled={reactivateMutation.isPending || deactivateMutation.isPending || deactivateBlocked}
      // Persistent helper text below is the accessible path for the blocked
      // reason: a native-disabled Button is unfocusable, so the hover-only
      // Tooltip is not keyboard/screen-reader discoverable on its own.
      ariaDescribedBy={deactivateBlocked ? 'deactivate-disabled-reason' : undefined}
      className="whitespace-nowrap"
    >
      {isLive ? 'Deactivate Dealer' : 'Reactivate Dealer'}
    </Button>
  );

  const deleteButton = (
    <Button
      size="sm"
      variant="danger"
      onClick={handleDelete}
      disabled={deleteMutation.isPending || hasChildDealers}
      ariaDescribedBy={hasChildDealers ? 'delete-disabled-reason' : undefined}
      className="whitespace-nowrap"
    >
      Delete Dealer
    </Button>
  );

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        <div>
          <h3 className="font-semibold mb-2">{isLive ? 'Deactivate Dealer' : 'Reactivate Dealer'}</h3>
          <div className="flex items-start gap-4 justify-between">
            <p className="text-sm text-mp-muted">
              {isLive
                ? 'Deactivating the dealer will mark them inactive and exclude them from active workflows until reactivated.'
                : 'Reactivating the dealer will restore them to active status.'}
            </p>
            <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
              {deactivateBlocked ? (
                <div className="flex flex-col items-end gap-1">
                  <Tooltip content={CHILDREN_TOOLTIP}>{statusButton}</Tooltip>
                  <p id="deactivate-disabled-reason" className="text-xs text-mp-muted text-right">
                    {CHILDREN_TOOLTIP}
                  </p>
                </div>
              ) : statusButton}
            </PermissionGuard>
          </div>
        </div>

        {reactivateMutation.isSuccess && (
          <Alert variant="success">
            <span>Dealer has been reactivated successfully.</span>
          </Alert>
        )}
        {deactivateMutation.isSuccess && (
          <Alert variant="success">
            <span>Dealer has been deactivated successfully.</span>
          </Alert>
        )}
        {actionError && (
          <Alert variant="error">
            <span>{actionError}</span>
          </Alert>
        )}
      </div>
      {!isLive && (
        <div className="border-t border-mp-border mt-8 pt-6 max-w-2xl space-y-2">
          <div>
            <h3 className="font-semibold mb-2">Delete Dealer</h3>
            <div className="flex items-start gap-4 justify-between">
              <p className="text-sm text-mp-muted">
                Deleting the dealer soft-deletes the record so it is hidden from listings. The row and any group relationships remain in the database; recovery requires a database administrator.
              </p>
              <PermissionGuard permission={{ resource: 'dealer', action: 'delete' }}>
                {hasChildDealers ? (
                  <div className="flex flex-col items-end gap-1">
                    <Tooltip content={CHILDREN_TOOLTIP}>{deleteButton}</Tooltip>
                    <p id="delete-disabled-reason" className="text-xs text-mp-muted text-right">
                      {CHILDREN_TOOLTIP}
                    </p>
                  </div>
                ) : deleteButton}
              </PermissionGuard>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
